/// <reference types="@angular/localize" />

import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { DmButton, DmInput } from '@digitale-menschen/component-library-angular';
import { Router, RouterLink } from '@angular/router';
import { form, FormField, FormRoot } from '@angular/forms/signals';
import { HttpClient } from '@angular/common/http';
import {
  authenticationControllerRequestPasswordResetV1,
  getLexplusRequestPasswordResetDtoInitialValue,
  lexplusRequestPasswordResetDtoSchema,
} from '@lexplus-authentication-api';
import { lastValueFrom } from 'rxjs';
import { API_CONFIGURATION } from '@lexplus-environments';
import { AUTH_ROUTE_IDS, LOCALIZED_AUTH_ROUTES } from '../../auth-route-const';
import { fieldErrorMessage, FormAlertComponent, LocalizedRouteService, notify } from '@lexplus-shared-lib';

@Component({
  selector: 'lp-request-password-reset-form',
  imports: [DmButton, DmInput, RouterLink, FormField, FormRoot, FormAlertComponent],
  templateUrl: './request-password-reset-form.component.html',
  styleUrl: './request-password-reset-form.component.scss',
})
export class LexplusRequestPasswordResetFormComponent {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfiguration = inject(API_CONFIGURATION);
  private readonly router = inject(Router);
  private readonly localizedRouteService = inject(LocalizedRouteService);

  public readonly isForAdmin = input<boolean>(false);

  private readonly resetModel = signal(getLexplusRequestPasswordResetDtoInitialValue());
  protected readonly resetForm = form(this.resetModel, lexplusRequestPasswordResetDtoSchema, {
    submission: { action: () => this.requestPasswordReset() },
  });

  protected readonly loadingStatus = signal<'loading' | 'success' | 'error' | null>(null);
  /** Request failure, shown inside the form instead of as a toast. */
  protected readonly formErrorMessage = signal<string | null>(null);

  protected readonly emailErrorMessage = computed(() =>
    fieldErrorMessage(this.resetForm.email(), $localize`:@@auth.requestPasswordReset.emailRequired:E-Mail-Adresse ist erforderlich.`),
  );

  /** Editing the address retracts the previous failure, so the alert never contradicts the input. */
  private readonly clearErrorOnEmailEditEffect = effect(() => {
    this.resetForm.email().value();
    this.formErrorMessage.set(null);
  });

  protected readonly loginRoute = `/${this.localizedRouteService.getLocalizedRoute(
    LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.ROOT],
    LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.LOGIN],
  )}`;

  /**
   * Handles the password reset request workflow by sending the user's email
   * to the authentication service for password reset initiation.
   */
  public async requestPasswordReset(): Promise<void> {
    this.loadingStatus.set('loading');
    // Cleared up front so a repeated failure re-mounts the alert, which both
    // re-announces it and makes the retry visible.
    this.formErrorMessage.set(null);

    try {
      await lastValueFrom(
        authenticationControllerRequestPasswordResetV1(this.httpClient, this.apiConfiguration.baseUrlAuth, {
          body: this.resetForm().value(),
        }),
      );

      this.loadingStatus.set('success');
      notify.success($localize`:@@auth.requestPasswordReset.success:Passwort-Reset angefordert`, {
        description: $localize`:@@auth.requestPasswordReset.successDescription:Bitte den E-Mail-Posteingang auf Anweisungen prüfen.`,
      });

      // The toast is rendered at app level and stays visible across the navigation.
      await this.router.navigateByUrl(this.loginRoute);
    } catch {
      this.loadingStatus.set('error');
      this.formErrorMessage.set($localize`:@@auth.requestPasswordReset.failedDescription:Bitte in einem Moment erneut versuchen.`);
    }
  }
}
